
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Calendar, Check, Clock, MessageSquare, Search, User, Smartphone, Instagram } from 'lucide-react';
import { useCreateScheduledMessage, useEditScheduledMessage, ScheduledMessage } from '@/hooks/useScheduledMessages';
import { useConfigApiOficial, useTemplatesOficial } from '@/hooks/useWhatsAppOficial';
import { useAgendaLeadSearch } from '@/hooks/useLeadsData';
import { Lead } from '@/hooks/useLeadsData';
import { useClinica } from '@/contexts/ClinicaContext';
import { getTodayInSaoPaulo, getCurrentTimeInSaoPaulo, convertUTCToSaoPaulo } from '@/utils/timezoneUtils';
import { DatePicker } from '@/components/ui/DatePicker';
import { TimePicker } from '@/components/ui/TimePicker';
import { EmojiPicker } from '@/components/ui/EmojiPicker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Modal para programar/editar envio de mensagens
 * 
 * O que faz:
 * - Permite agendar novas mensagens ou editar existentes
 * - Converte corretamente horários entre São Paulo e UTC
 * - Valida datas e horários baseado no fuso de São Paulo
 * 
 * Props:
 * - isOpen: controla visibilidade do modal
 * - onClose: função para fechar modal
 * - selectedLead?: lead pré-selecionado (opcional)
 * - editMessage?: mensagem para editar (opcional)
 * - mode: 'create' | 'edit' - modo do modal
 */

interface ScheduleMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLead?: Lead | null;
  editMessage?: ScheduledMessage | null;
  mode?: 'create' | 'edit';
}

export const ScheduleMessageModal: React.FC<ScheduleMessageModalProps> = ({
  isOpen,
  onClose,
  selectedLead = null,
  editMessage = null,
  mode = 'create'
}) => {
  const [leadId, setLeadId] = useState('');
  const [leadNome, setLeadNome] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [dataAgendada, setDataAgendada] = useState('');
  const [horaAgendada, setHoraAgendada] = useState('');
  const [templateOficialId, setTemplateOficialId] = useState('');
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const commandInputRef = useRef<HTMLInputElement>(null);
  const { clinicaId: clinicaIdUsuario } = useClinica();
  // Templates, remetente e clientes precisam pertencer à mesma clínica do contato aberto.
  const clinicaId = selectedLead ? selectedLead.clinica_id : editMessage ? editMessage.clinica_id : clinicaIdUsuario;
  const isEditMode = mode === 'edit' && editMessage;
  // Só busca contatos com o modal aberto e sem lead já escolhido.
  // Motivo: o useLeads() antigo baixava a base inteira ao abrir Contatos e estourava o timeout do banco.
  const precisaBuscarContatos = isOpen && !selectedLead && !isEditMode;
  const { data: leads = [], isFetching: buscandoContatos } = useAgendaLeadSearch(
    clinicaId ?? undefined,
    searchQuery,
    leadId || undefined,
    precisaBuscarContatos,
  );
  const createMutation = useCreateScheduledMessage(clinicaId);
  const editMutation = useEditScheduledMessage();
  const { data: configOficial } = useConfigApiOficial(clinicaId ?? undefined);
  const { data: templatesOficiais = [] } = useTemplatesOficial(clinicaId ?? undefined);
  const oficialAtivo = configOficial?.config?.ativo === true;
  const templatesAprovados = templatesOficiais.filter((item) => item.status === 'APPROVED');

  // Encontra o lead ativo para exibir dados completos (telefone, origem)
  const activeLead = selectedLead || (leadId ? leads.find(l => l.id === leadId) : null);

  // Pré-preencher dados do lead se fornecido
  useEffect(() => {
    if (selectedLead) {
      setLeadId(selectedLead.id);
      setLeadNome(selectedLead.nome || 'Lead sem nome');
    } else if (isEditMode && editMessage) {
      setLeadId(editMessage.lead_id);
      setLeadNome(editMessage.lead_nome);
    } else {
      setLeadId('');
      setLeadNome('');
    }
  }, [selectedLead, editMessage, mode]);

  // Pré-preencher dados para edição
  useEffect(() => {
    if (isEditMode && editMessage) {
      setMensagem(editMessage.mensagem);
      
      // Converter UTC do banco para horário de São Paulo
      const saoPauloDate = convertUTCToSaoPaulo(editMessage.data_agendada);
      setDataAgendada(saoPauloDate.toISOString().split('T')[0]);
      setHoraAgendada(saoPauloDate.toTimeString().slice(0, 5));
    }
  }, [editMessage, mode]);

  // Resetar form ao fechar
  useEffect(() => {
    if (!isOpen) {
      if (!selectedLead && !isEditMode) {
        setLeadId('');
        setLeadNome('');
      }
      if (!isEditMode) {
        setMensagem('');
        setDataAgendada('');
        setHoraAgendada('');
      }
    }
  }, [isOpen, selectedLead, mode]);

  // Filtrar leads com telefone ou que são do Instagram
  const leadsDisponiveis = leads.filter(lead => lead.telefone || lead.origem_lead?.toLowerCase().includes('instagram'));

  // Validações
  const isFormValid = () => {
    if (!leadId || !mensagem.trim() || !dataAgendada || !horaAgendada) {
      return false;
    }

    // Verificar se data/hora é futura (baseado em SP)
    const agendamentoSP = `${dataAgendada}T${horaAgendada}`;
    const agendamentoDate = new Date(agendamentoSP);
    const nowSP = new Date();
    // Ajustar para horário de SP
    const saoPauloNow = new Date(nowSP.getTime() - (3 * 60 * 60 * 1000));
    
    return agendamentoDate > saoPauloNow;
  };

  // Função para agendar/editar mensagem
  const handleSave = async () => {
    if (!isFormValid()) return;
    
    const dataHoraCompleta = `${dataAgendada}T${horaAgendada}`;
    
    try {
      if (isEditMode && editMessage) {
        await editMutation.mutateAsync({
          id: editMessage.id,
          mensagem: mensagem.trim(),
          data_agendada: dataHoraCompleta
        });
      } else {
        await createMutation.mutateAsync({
          lead_id: leadId,
          lead_nome: leadNome,
          mensagem: mensagem.trim(),
          data_agendada: dataHoraCompleta,
          template_oficial_id: templateOficialId || null,
        });
      }
      onClose();
    } catch (error) {
      console.error('Erro ao salvar mensagem:', error);
    }
  };

  // Função para atualizar nome do lead quando selecionado
  const handleLeadChange = (selectedLeadId: string) => {
    setLeadId(selectedLeadId);
    const lead = leadsDisponiveis.find(l => l.id === selectedLeadId);
    setLeadNome(lead?.nome || 'Lead sem nome');
    setOpen(false);
    setSearchQuery('');
  };

  // Focar no input de busca quando abrir o popover (importante para mobile)
  useEffect(() => {
    if (open && commandInputRef.current) {
      // Pequeno delay para garantir que o popover está renderizado
      setTimeout(() => {
        commandInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Filtrar leads baseado na busca
  const filteredLeads = leadsDisponiveis.filter(lead => {
    const searchLower = searchQuery.toLowerCase();
    const nomeMatch = lead.nome?.toLowerCase().includes(searchLower);
    const telefoneMatch = lead.telefone?.toLowerCase().includes(searchLower);
    const origemMatch = lead.origem_lead?.toLowerCase().includes(searchLower);
    return nomeMatch || telefoneMatch || origemMatch;
  });

  // Obter data mínima (hoje em SP)
  const getMinDate = () => {
    return getTodayInSaoPaulo();
  };

  // Obter hora mínima (se for hoje em SP)
  const getMinTime = () => {
    if (dataAgendada === getTodayInSaoPaulo()) {
      return getCurrentTimeInSaoPaulo();
    }
    return '00:00';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-accent-foreground" />
            {isEditMode ? 'Editar Mensagem Agendada' : 'Programar Mensagem'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Aviso sobre uso moderado */}
          <Alert className="border-warning/30 bg-warning-subtle rounded-xl">
            <AlertTriangle className="h-4 w-4 text-warning-text" />
            <AlertDescription className="text-warning-text">
              <strong>Use com moderação!</strong> Disparos em massa podem ocasionar bloqueios do WhatsApp. 
              Recomendamos não mais que 50 mensagens por dia por número.
            </AlertDescription>
          </Alert>

          {/* Seletor de Lead (só aparece se não houver lead pré-selecionado e não for edição) */}
          {!selectedLead && !isEditMode && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Selecionar Contato
              </Label>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between min-h-[44px] text-left font-normal"
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <Search className="h-4 w-4 shrink-0 opacity-50" />
                      <span className="truncate">
                        {leadId
                          ? leadsDisponiveis.find((lead) => lead.id === leadId)?.nome || 'Lead sem nome'
                          : "Buscar contato..."}
                      </span>
                    </div>
                    <span className="ml-2 h-4 w-4 shrink-0 opacity-50">▼</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      ref={commandInputRef}
                      placeholder="Digite para buscar..."
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                    />
                    <CommandList className="max-h-[300px] overflow-y-auto overflow-x-hidden">
                      {buscandoContatos ? (
                        <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">
                          Buscando contatos...
                        </CommandEmpty>
                      ) : filteredLeads.length === 0 ? (
                        <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">
                          Nenhum contato encontrado.
                        </CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {filteredLeads.map((lead) => (
                            <CommandItem
                              key={lead.id}
                              value={lead.id}
                              onSelect={() => handleLeadChange(lead.id)}
                              className="min-h-[44px] cursor-pointer py-2"
                            >
                              <div className="flex items-center justify-between w-full gap-2">
                                <div className="flex items-center gap-2 overflow-hidden flex-1">
                                  <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                                  <span className="truncate font-medium">{lead.nome || 'Lead sem nome'}</span>
                                  {lead.telefone && (
                                    <span className="text-xs text-muted-foreground shrink-0">
                                      {lead.telefone}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {lead.telefone ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-info-text bg-info-subtle px-2 py-0.5 rounded-full">
                                      <Smartphone className="h-3 w-3" />
                                      WhatsApp
                                    </span>
                                  ) : lead.origem_lead?.toLowerCase().includes('instagram') ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-accent-foreground bg-accent px-2 py-0.5 rounded-full">
                                      <Instagram className="h-3 w-3" />
                                      Instagram
                                    </span>
                                  ) : null}
                                  {leadId === lead.id && (
                                    <Check className="h-4 w-4 text-accent-foreground" />
                                  )}
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Clique e digite para buscar por nome, telefone ou origem
              </p>
            </div>
          )}

          {/* Mostrar lead selecionado */}
          {(selectedLead || isEditMode) && leadId && (
            <div className="flex items-center gap-3 p-3 bg-muted/80 border border-border rounded-2xl">
              <div className="w-10 h-10 rounded-full bg-accent border border-primary/40 flex items-center justify-center shrink-0">
                <User className="w-5 h-5 text-accent-foreground" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{leadNome}</p>
                {activeLead?.telefone && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <img src="/whatsapp.svg" alt="WhatsApp" className="w-3.5 h-3.5" />
                    <span className="text-xs text-muted-foreground">
                      {activeLead.telefone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3').replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')}
                    </span>
                  </div>
                )}
                {!activeLead?.telefone && activeLead?.origem_lead?.toLowerCase().includes('instagram') && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Instagram className="w-3 h-3 text-accent-foreground" />
                    <span className="text-xs text-muted-foreground">Instagram</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Campo de mensagem */}
          <div className="space-y-2">
            <Label htmlFor="mensagem" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Mensagem
            </Label>
            <div className="relative">
              <Textarea 
                id="mensagem" 
                placeholder="Digite sua mensagem aqui..." 
                value={mensagem} 
                onChange={(e) => setMensagem(e.target.value)} 
                maxLength={4000} 
                className="min-h-[100px] resize-none rounded-xl pr-12" 
              />
              <div className="absolute bottom-2 right-2">
                <EmojiPicker onSelect={(emoji) => setMensagem((prev) => prev + emoji)} />
              </div>
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Limite: 4000 caracteres</span>
              <span>{mensagem.length}/4000</span>
            </div>
            {oficialAtivo && !isEditMode && (
              <div className="space-y-2 pt-2">
                <Label>Se a conversa passar de 24 horas, usar este modelo (opcional)</Label>
                <Select value={templateOficialId} onValueChange={setTemplateOficialId}>
                  <SelectTrigger><SelectValue placeholder="Nenhum — só texto livre na janela de 24h" /></SelectTrigger>
                  <SelectContent>
                    {templatesAprovados.map((modelo) => (
                      <SelectItem key={modelo.id} value={modelo.id}>{modelo.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Seletor de Data e Hora */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Seletor de Data */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Calendar className="w-4 h-4 text-accent-foreground" />
                Seletor de Data
              </Label>
              <DatePicker
                value={dataAgendada}
                onChange={setDataAgendada}
                minDate={getMinDate()}
              />
            </div>

            {/* Seletor de Hora */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Clock className="w-4 h-4 text-accent-foreground" />
                Seletor de Hora
              </Label>
              <TimePicker
                value={horaAgendada}
                onChange={setHoraAgendada}
                minTime={getMinTime()}
              />
            </div>
          </div>

          {/* Card "Será enviado em..." */}
          {leadNome && mensagem && dataAgendada && horaAgendada && (
            <div className="flex items-center p-2.5 bg-accent/80 border border-primary/40 rounded-xl">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 rounded-md bg-accent flex items-center justify-center shrink-0">
                  <Calendar className="w-3.5 h-3.5 text-accent-foreground" />
                </div>
                <p className="text-sm">
                  <span className="text-accent-foreground font-medium">Será enviado em:</span>{' '}
                  <span className="text-muted-foreground">
                    {format(new Date(`${dataAgendada}T${horaAgendada}`), "EEEE, d 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Botões de ação */}
        <div className="flex justify-end space-x-2 pt-4">
          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={createMutation.isPending || editMutation.isPending} 
            className="rounded-xl"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!isFormValid() || createMutation.isPending || editMutation.isPending} 
            className="bg-primary text-primary-foreground rounded-full font-semibold hover:bg-brand-soft"
          >
            {createMutation.isPending || editMutation.isPending ? 
              (isEditMode ? 'Salvando...' : 'Agendando...') : 
              (isEditMode ? 'Salvar Alterações' : 'Agendar Mensagem')
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
